import { Box, Typography, Button, CircularProgress, Divider, Table, TableHead, TableBody, TableRow, TableCell, Paper } from '@mui/material';
import { AutoAwesome as AIIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ProGate } from './ProGate';
import type { JournalEntryFeedback } from '../../types/api.types';

const mdComponents: Components = {
  h1: ({ children }) => <Typography variant="h6" gutterBottom>{children}</Typography>,
  h2: ({ children }) => <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, mb: 0.5 }}>{children}</Typography>,
  h3: ({ children }) => <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 1.5, mb: 0.5 }}>{children}</Typography>,
  p:  ({ children }) => <Typography variant="body2" paragraph sx={{ mb: 1 }}>{children}</Typography>,
  li: ({ children }) => <Typography component="li" variant="body2" sx={{ ml: 2, mb: 0.25 }}>{children}</Typography>,
  ul: ({ children }) => <Box component="ul" sx={{ pl: 2, mt: 0, mb: 1 }}>{children}</Box>,
  ol: ({ children }) => <Box component="ol" sx={{ pl: 2, mt: 0, mb: 1 }}>{children}</Box>,
  hr: () => <Divider sx={{ my: 1.5 }} />,
  strong: ({ children }) => <Box component="strong" sx={{ fontWeight: 'bold' }}>{children}</Box>,
  em: ({ children }) => <Box component="em" sx={{ fontStyle: 'italic' }}>{children}</Box>,
  table: ({ children }) => <Paper variant="outlined" sx={{ overflowX: 'auto', mb: 2 }}><Table size="small">{children}</Table></Paper>,
  thead: ({ children }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => <TableCell sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>{children}</TableCell>,
  td: ({ children }) => <TableCell>{children}</TableCell>,
};

interface Props {
  isPro: boolean;
  feedback: JournalEntryFeedback | null;
  onRequestReview: () => void;
  isLoading: boolean;
  entryHasBody: boolean;
}

export const FeedbackPanel = ({ isPro, feedback, onRequestReview, isLoading, entryHasBody }: Props) => {
  if (!isPro) return <ProGate />;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AIIcon color="primary" /> AI Feedback
        </Typography>
        <Button
          variant={feedback ? 'outlined' : 'contained'}
          startIcon={feedback ? <RefreshIcon /> : <AIIcon />}
          onClick={onRequestReview}
          disabled={isLoading || !entryHasBody}
          size="small"
        >
          {isLoading ? 'Reviewing…' : feedback ? 'Re-review' : 'Request Review'}
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && feedback && (
        <>
          <Divider />
          <Box>
            <ReactMarkdown components={mdComponents} remarkPlugins={[remarkGfm]}>{feedback.feedbackText}</ReactMarkdown>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Reviewed {new Date(feedback.createdAt).toLocaleString()}
          </Typography>
        </>
      )}

      {!isLoading && !feedback && (
        <Typography variant="body2" color="text.secondary">
          Save your entry then click "Request Review" to get feedback from Claude.
        </Typography>
      )}
    </Box>
  );
};
